import { TranslateService } from '@ngx-translate/core';
import { MarkerTool } from 'src/app/classes/forge/MarkerTool';
import { ZoneShape } from 'src/app/classes/viewer/shapes/zone';
import { Model, ModelOrigin } from 'src/app/interfaces/api/model';
import { PostZone, Zone } from 'src/app/interfaces/api/zone';
import { ZoneService } from 'src/app/services/api/zones/zone.service';

const Autodesk = (window as any).Autodesk;

export class ToolsDockingPanel extends Autodesk.Viewing.UI.DockingPanel {
    viewer: any;
    options: any;
    zoneService: ZoneService;
    translate: TranslateService;
    markerTool: MarkerTool | null = null;
    zones: ZoneShape[] = [];
    colors: Record<string, { name: string; value: number }>;
    model: Model;
    origin: ModelOrigin;
    offset: { x: number; y: number; z: number } = {
        x: 0, y: 0, z: 0
    };
    previousState: any;
    zoneExt: any;
    visible: boolean = true;
    marks: any[] = [];
    polyPoints: any[] = [];
    undoList: any[] = [];
    toolName: string = "MarkerTool";

    constructor(viewer: any, container: HTMLElement, id: string, title: string, options: any) {
        super(container, id, title, options);

        const titleEl = (this as any).container?.querySelector('.docking-panel-title');
        if (titleEl) {
            titleEl.removeAttribute('data-i18n');
            titleEl.textContent = title;
        }

        this.viewer = viewer;
        this.options = options;
        this.model = options.model;
        this.origin = this.model.origin;
        this.zones = options.zones;

        const styleId = 'tools-style';

        if (!document.getElementById(styleId)) {
            const link = document.createElement('link');
            link.id = styleId;
            link.rel = 'stylesheet';
            link.href = 'viewer/zones/tools/tools.css';
            document.head.appendChild(link);
        }

        if (options.visible != undefined)
            this.visible = options.visible;

        let offset = this.viewer.model.getData().globalOffset;

        if (offset)
            this.offset = offset;

        this.zoneService = options.zonesService || options.zoneService;
        this.translate = options.translate;

        this.colors = {
            "danger": { name: `Danger (${this.translate.instant('colors.red')}`, value: 0xFF0000 },
            "warning": { name: `Warning (${this.translate.instant('colors.orange')}`, value: 0xFFA500 },
            "primary": { name: `Primary (${this.translate.instant('colors.blue')}`, value: 0x0000FF },
            "success": { name: `Success (${this.translate.instant('colors.green')}`, value: 0x008000 },
            "lemon": { name: `Lemon (${this.translate.instant('colors.yellow')}`, value: 0xf3e612 },
            "teal": { name: `Teal (${this.translate.instant('colors.teal')}`, value: 0x39CCCC },
            "fuchsia": { name: `Fuchsia (${this.translate.instant('colors.fuchsia')}`, value: 0xF012BE },
            "info": { name: `Info (${this.translate.instant('colors.lightGrey')}`, value: 0xDDDDDD },
            "dark": { name: `Dark (${this.translate.instant('colors.black')}`, value: 0x000000 },
            "muted": { name: `Muted (${this.translate.instant('colors.grey')}`, value: 0x808080 }
        };

        this['container'].classList.add('docking-panel-container-solid-color-a');
        this['container'].style.top = "10px";
        this['container'].style.right = "10px";
        this['container'].style.width = "237px";
        this['container'].style.height = "284px";

        const div = document.createElement('div');
        div.style.margin = '0';

        this['container'].appendChild(div);

        this.loadAndRenderHtml(div).then(() => {
            this.init();

            // Sécurisation des listeners
            let saveBtn = document.getElementById('save-zone');
            if (saveBtn) {
                saveBtn.removeEventListener('click', this.onSave);
                saveBtn.addEventListener('click', () => this.onSave());
            }

            let addBtn = document.getElementById('add-marker');
            if (addBtn) {
                addBtn.removeEventListener('click', this['onAddMarker']);
                addBtn.addEventListener('click', () => {
                    if (this.markerTool) {
                        if (this.viewer.toolController.getActiveTool().toolName === this.markerTool.toolName) {
                            this.viewer.toolController.deactivateTool(this.markerTool.toolName);
                            addBtn.classList.remove('active');
                        } else {
                            this.viewer.toolController.activateTool(this.markerTool.toolName);
                            addBtn.classList.add('active');
                        }
                    }
                });
            }

            let undoBtn = document.getElementById('undo-zone');
            if (undoBtn) {
                undoBtn.removeEventListener('click', this.onUndo);
                undoBtn.addEventListener('click', () => this.onUndo());
            }

            let redoBtn = document.getElementById('redo-zone');
            if (redoBtn) {
                redoBtn.removeEventListener('click', this.onRedo);
                redoBtn.addEventListener('click', () => this.onRedo());
            }

            let closeBtn = document.querySelector('#zoneToolsDockingPanel .docking-panel-close');
            if (closeBtn) {
                closeBtn.removeEventListener('click', this.onClose);
                closeBtn.addEventListener('click', () => this.onClose());
            }
        });

        let markerOptions = {
            ...this.options,
            viewer: this.viewer
        }

        this.markerTool = new MarkerTool(markerOptions);
        this.viewer.toolController.registerTool(this.markerTool);
    }

    init() {
        this.zoneExt = this.viewer.loadedExtensions['ZoneExtension'];
        this.zoneExt?.dockingPanel?.setVisible(false);
        // save the viewer state
        this.previousState = this.viewer.getState();

        if (!this.viewer.model.getData().is2d) {
            // set the background color to white
            this.viewer.setBackgroundColor(255, 255, 255, 255, 255, 255);

            let viewcuiext = this.viewer.getExtension('Autodesk.ViewCubeUi')
            viewcuiext.setViewCube('top');
            // change to top view
            // this.viewer.setViewCube('top');
            this.viewer.loadExtension('Autodesk.NPR').then(() => {
                let viewNPRext = this.viewer.getExtension('Autodesk.NPR')
                // line-style effect
                viewNPRext.setParameter("style", "graphite");

                // image brightness = 1
                viewNPRext.setParameter("brightness", 1.0);
                // // change to Orthographic mode
                this.viewer.navigation.toOrthographic();
            })
        }
    }

    onSave(): void {
        if (!this.markerTool) {
            console.error('MarkerTool not initialized');
            return;
        }

        const sketchs = this.markerTool.finish();
        if (sketchs.local.length < 3) {
            window.alert("Zone must have at least 3 points");
            this.setVisible(false);
            return;
        }

        let height = 10;
        let n = this.zones.length;
        let name = "Untitled-" + n;

        while (this.zones.find((z: any) => z.name == name)) {
            n++;
            name = "Untitled-" + n;
        }

        let params = {
            viewer: this.viewer,
            model: this.model,
            polygon: sketchs.local,
            z: this.origin.position?.z || 0,
            name,
            color: 'primary' as const,
            alpha: 0.5,
            height,
            visible: this.visible,
            number: this.zones.length,
            type: 'polygon' as const,
            id: '',
            onAfterUpdate: function () {
            }
        }

        // create a new zone
        const zone = new ZoneShape(params);

        // save
        const zoneJSON = zone.toJSON();

        const zoneData: PostZone = {
            name: zoneJSON.name,
            number: zoneJSON.number,
            type: 'polygon',
            color: zoneJSON.color as "danger" | "warning" | "primary" | "success" | "info" | "dark" | "muted",
            alpha: zoneJSON.alpha,
            z: zoneJSON.z,
            height: zoneJSON.height,
            polygon: zone.getTransformedPoints(),
            visible: zoneJSON.visible
        };

        this.zoneService.addZone(zoneData).subscribe({
            next: (newZone: Zone) => {
                this.onClose();
            },
            error: (err: any) => {
                console.error(err);
            }
        });
    }

    onUndo(): void {
        if (this.markerTool) {
            this.markerTool.undoLastMark();
        }
    }

    onRedo(): void {
        if (this.markerTool) {
            this.markerTool.redoLastMark();
        }
    }

    onClose(): void {
        this.setVisible(false);

        if (this.zoneExt?.dockingPanel) {
            this.zoneExt.dockingPanel.setVisible(true);
            this.zoneExt.init();
        }

        if (this.markerTool) {
            let addBtn: HTMLElement | null = document.getElementById('add-marker');

            if (addBtn) {
                addBtn.classList.remove('active');
            }

            this.markerTool.undoList = [];
            this.markerTool.polyPoints = [];
            this.markerTool.removeLines();
            this.markerTool.removeAllMarks();
            this.viewer.toolController.deactivateTool(this.markerTool.toolName);
        }

        let viewNPRext = this.viewer.getExtension('Autodesk.NPR');

        if (viewNPRext) {
            viewNPRext.setParameter("style", "");
        }

        if (this.previousState) {
            this.viewer.restoreState(this.previousState);
        }
    }

    setVisible(bool: boolean) {
        super.setVisible(bool);
        this.visible = bool;
    }

    async loadAndRenderHtml(div: HTMLDivElement) {
        const template = await this.loadHtmlTemplate('viewer/zones/tools/tools.html');
        const variables = {

        };

        div.innerHTML = this.renderTemplate(template, variables);
    }

    async loadHtmlTemplate(path: string): Promise<string> {
        const response = await fetch(path);
        return await response.text();
    }

    renderTemplate(template: string, variables: Record<string, string | number>) {
        return template.replace(/{{(.*?)}}/g, (_, key) => String(variables[key.trim()] ?? ''));
    }
}
